import { gsap } from "gsap";
import React, { useEffect, useRef, useState } from "react";

const WORDS = [
  { text: "Human-Computer Interaction", size: "xl", color: "theme",  definition: "The study of how people interact with computers and digital systems, with the goal of designing usable, efficient, and enjoyable experiences." },
  { text: "Artificial Intelligence",    size: "xl", color: "theme",  definition: "Technology that enables machines to simulate human intelligence — learning, reasoning, and problem-solving — to automate and augment human tasks." },
  { text: "Product Design",             size: "xl", color: "theme",  definition: "The end-to-end process of identifying a user problem and crafting a solution through research, ideation, prototyping, and iteration." },
  { text: "Empathy",                    size: "xl", color: "theme",  definition: "The ability to understand and share the feelings and perspectives of users, forming the foundation of human-centered design." },
  { text: "User Research",              size: "lg", color: "theme",  definition: "Systematic study of target users — their behaviors, needs, and motivations — to inform design decisions with real evidence." },
  { text: "Interaction Design",         size: "lg", color: "theme",  definition: "Designing the interactive behaviors of digital products, focusing on how users engage with them over time through clicks, gestures, and flows." },
  { text: "Data Analysis",              size: "lg", color: "theme",  definition: "The process of inspecting, cleaning, and modeling data to discover useful patterns and support informed design and business decisions." },
  { text: "Design Thinking",            size: "md", color: "bright", definition: "A human-centered problem-solving approach that moves through empathize, define, ideate, prototype, and test to reach innovative solutions." },
  { text: "Usability Testing",          size: "md", color: "bright", definition: "Evaluating a product by observing representative users attempt real tasks, then using findings to remove friction and improve the experience." },
  { text: "Prototyping",                size: "md", color: "bright", definition: "Creating early models of a design — from paper sketches to interactive mockups — to explore ideas and gather feedback before building." },
  { text: "Systems Thinking",           size: "md", color: "bright", definition: "Understanding how a product's components interrelate and how changes in one area ripple through the broader system." },
  { text: "Journey Mapping",            size: "md", color: "bright", definition: "A visualization of the end-to-end experience a user has with a product or service, highlighting emotions, pain points, and opportunities." },
  { text: "Content Strategy",           size: "md", color: "bright", definition: "Planning, creating, and governing content so it meets user needs and business goals across every touchpoint." },
  { text: "Visual Design",              size: "md", color: "bright", definition: "Using color, typography, imagery, and layout to communicate clearly and create an aesthetically cohesive user interface." },
  { text: "Information Architecture",   size: "sm", color: "body",   definition: "The structural design of content and navigation so users can find what they need quickly and intuitively." },
  { text: "Wireframing",                size: "sm", color: "body",   definition: "Low-fidelity blueprints that define the layout and structure of a UI before investing in visual polish or development." },
  { text: "Accessibility",              size: "sm", color: "body",   definition: "Designing products that can be used by people of all abilities, following standards like WCAG to ensure inclusive experiences." },
  { text: "Personas",                   size: "sm", color: "body",   definition: "Fictional but research-backed representations of key user segments used to align teams and guide design decisions." },
  { text: "User Flows",                 size: "sm", color: "body",   definition: "Diagrams that map the steps a user takes through a product to complete a goal, revealing gaps and friction points." },
  { text: "Contextual Inquiry",         size: "sm", color: "body",   definition: "A research method where designers observe and interview users in their own environment to uncover real-world behaviors and needs." },
  { text: "Design Systems",             size: "sm", color: "body",   definition: "A collection of reusable components, tokens, and guidelines that keep product design consistent and speed up development." },
  { text: "Front-End Development",      size: "sm", color: "body",   definition: "Building the visual and interactive layer of a website or app — the part users see and touch — using HTML, CSS, and JavaScript." },
  { text: "Stakeholder Management",     size: "sm", color: "body",   definition: "Identifying, engaging, and aligning people with a stake in a project to build consensus and keep design moving forward." },
  { text: "Cognitive Load",             size: "xs", color: "muted",  definition: "The mental effort required to process information. Good design reduces unnecessary load so users can focus on their actual goal." },
  { text: "Mental Models",              size: "xs", color: "muted",  definition: "The internal assumptions users have about how a system works. Designs that match mental models feel intuitive and require less learning." },
  { text: "A/B Testing",                size: "xs", color: "muted",  definition: "Comparing two design variants with real users to determine which performs better against a defined metric." },
  { text: "Heuristics",                 size: "xs", color: "muted",  definition: "General usability principles — like Nielsen's 10 heuristics — used to evaluate designs and spot common usability problems quickly." },
  { text: "Card Sorting",               size: "xs", color: "muted",  definition: "A research technique where users group topics into categories, revealing how they think about information to guide navigation design." },
  { text: "Task Analysis",              size: "xs", color: "muted",  definition: "Breaking down a user's goal into individual steps to understand how they currently accomplish tasks and where a design can help." },
  { text: "Affordances",                size: "xs", color: "muted",  definition: "Visual or physical properties of an element that signal how it can be used — like a button that looks pressable." },
  { text: "Adtech",                     size: "xs", color: "muted",  definition: "Advertising technology — the digital tools and platforms used to target, deliver, measure, and optimize online advertising." },
  { text: "Fintech",                    size: "xs", color: "muted",  definition: "Financial technology — software and services that improve or automate financial processes like banking, payments, and investing." },
  { text: "Medtech",                    size: "xs", color: "muted",  definition: "Medical technology — devices and software used in healthcare settings to improve diagnosis, treatment, and patient outcomes." },
  { text: "Iteration",                  size: "xs", color: "muted",  definition: "The practice of repeatedly refining a design based on feedback and testing, producing progressively better solutions." },
  { text: "B2B / B2C",                  size: "xs", color: "muted",  definition: "Business-to-Business and Business-to-Consumer models — distinct audiences with different motivations, requiring tailored design approaches." },
  { text: "Eye Tracking",               size: "xs", color: "muted",  definition: "A research method that records where users look on a screen, revealing visual attention patterns and guiding layout decisions." },
  { text: "Competitive Analysis",       size: "xs", color: "muted",  definition: "Evaluating competitor products to identify strengths, weaknesses, patterns, and opportunities to differentiate your design." },
  { text: "Responsive Design",          size: "xs", color: "muted",  definition: "Designing interfaces that adapt fluidly to different screen sizes and devices, ensuring a consistent experience everywhere." },
  { text: "Workshop Facilitation",      size: "xs", color: "muted",  definition: "Leading structured collaborative sessions to align teams, generate ideas, or solve design problems together." },
];

const SIZE_MAP = {
  xl: "2.2rem",
  lg: "1.5rem",
  md: "1.1rem",
  sm: "0.9rem",
  xs: "0.75rem",
};

// Maps to CSS custom properties defined in _wordcloud.scss (overridden in _light.scss)
const COLOR_MAP = {
  theme:  "var(--wc-theme)",
  bright: "var(--wc-bright)",
  body:   "var(--wc-body)",
  muted:  "var(--wc-muted)",
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Pre-compute shuffle and rotations once at module load so re-renders don't cause jumps
const SHUFFLED = shuffle(WORDS).map((w) => ({
  ...w,
  rotation: (Math.random() * 10 - 5).toFixed(1),
}));

function WordCloud() {
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null); // { text, definition, x, y }

  useEffect(() => {
    const words = containerRef.current?.querySelectorAll(".mi-wordcloud-word");
    if (!words?.length) return;

    gsap.fromTo(
      words,
      { opacity: 0, y: 18, scale: 0.85 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.55,
        ease: "back.out(1.4)",
        stagger: { each: 0.06, from: "random" },
      }
    );
  }, []);

  const handleWordClick = (e, word) => {
    e.stopPropagation();
    if (tooltip?.text === word.text) {
      setTooltip(null);
      return;
    }
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const wordRect = e.currentTarget.getBoundingClientRect();
    const x = wordRect.left - containerRect.left + wordRect.width / 2;
    const y = wordRect.top - containerRect.top;
    setTooltip({ text: word.text, definition: word.definition, x, y });
  };

  return (
    <div
      ref={containerRef}
      className="mi-wordcloud"
      onClick={() => setTooltip(null)}
    >
      {SHUFFLED.map((w, i) => (
        <span
          key={i}
          className={`mi-wordcloud-word${tooltip?.text === w.text ? " is-active" : ""}`}
          style={{
            fontSize: SIZE_MAP[w.size],
            color: COLOR_MAP[w.color],
            transform: `rotate(${w.rotation}deg)`,
          }}
          onClick={(e) => handleWordClick(e, w)}
        >
          {w.text}
        </span>
      ))}

      {tooltip && (
        <div
          className="mi-wordcloud-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <strong>{tooltip.text}</strong>
          <p>{tooltip.definition}</p>
        </div>
      )}
    </div>
  );
}

export default WordCloud;
